import React, { useState, useMemo } from 'react';
import { LeaveStatus, LeaveAdminUpdateDTO, LeaveDto } from '../../../../types/leaves.types';
import { LEAVE_STATUSES } from '../../../../constants/leaveConstants';
import { Select, Popconfirm } from 'antd';
import { SelectProps } from 'antd/lib/select';
import './LeaveStatusDropdown.css'; // Eğer gerekirse bir CSS dosyası ekleyebiliriz
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../store/store';

interface LeaveStatusDropdownProps {
  status: LeaveStatus;
  record: LeaveDto;
  onStatusChange: (dto: LeaveAdminUpdateDTO) => void;
  disabled?: boolean;
  text?: boolean;
  canUpdateStatus?: boolean;
}

const LeaveStatusDropdown: React.FC<LeaveStatusDropdownProps> = ({
  status: overallStatus,
  record,
  onStatusChange,
  disabled = false,
  text = true,
  canUpdateStatus = true
}) => {
  const { t } = useTranslation();
  const { user } = useSelector((state: RootState) => state.auth);
  const [selectedValue, setSelectedValue] = useState<LeaveStatus | null>(null);
  const [isPopconfirmVisible, setIsPopconfirmVisible] = useState(false);

  const displayStatus = useMemo(() => {
    // Eğer izin nihai olarak onaylanmış veya reddedilmişse, herkes için bu durumu göster.
    if (overallStatus === LeaveStatus.APPROVED || overallStatus === LeaveStatus.REJECTED) {
      return overallStatus;
    }

    if (record.approvalFlow && user) {
      const userStep = record.approvalFlow.steps.find(step => step.approver.id === user.id);
      if (userStep) {
        switch (userStep.status) {
          case 'approved':
            return LeaveStatus.APPROVED;
          case 'rejected':
            return LeaveStatus.REJECTED;
          case 'pending_approval':
          case 'pending':
            return LeaveStatus.PENDING;
          default:
            return overallStatus;
        }
      }
    }
    return overallStatus;
  }, [overallStatus, record, user]);

  const isActionable = useMemo(() => {
    if (!canUpdateStatus || disabled) return false;

    // Approved durumda admin, pending veya rejected'e çekebilir.
    if (overallStatus === LeaveStatus.APPROVED) {
      return true;
    }

    // Rejected durumda işlem yapılamaz.
    if (overallStatus === LeaveStatus.REJECTED) {
      return false;
    }

    if (record.approvalFlow && user) {
      const steps = record.approvalFlow.steps;
      const userStep = steps.find(step => step.approver.id === user.id);
      
      // Kullanıcı adımını bulamazsak veya işlem yapamazsa, pasif
      if (!userStep) return false;

      // Kullanıcı son onaycı mı?
      const isLastApprover = steps.length > 0 && steps[steps.length - 1].approver.id === user.id;

      // Eğer son onaycı ise, kendi adımı onaylanmadığı sürece her zaman işlem yapabilir.
      if (isLastApprover) {
        return userStep.status !== 'approved' && userStep.status !== 'rejected';
      }

      // Son onaycı değilse, sadece sırası geldiğinde işlem yapabilir.
      return userStep.status === 'pending_approval';
    }

    // Hiyerarşik olmayan izinler için eski mantık
    return overallStatus === LeaveStatus.PENDING;
  }, [canUpdateStatus, disabled, overallStatus, record, user]);

  const getStatusColor = (status: LeaveStatus) => {
    const colors: Record<LeaveStatus, string> = {
      [LeaveStatus.PENDING]: '#f0ad4e',
      [LeaveStatus.APPROVED]: '#5cb85c',
      [LeaveStatus.REJECTED]: '#d9534f',
      [LeaveStatus.PENDING_HIERARCHY_APPROVAL]: '#f0ad4e',
    };
    return colors[status];
  };

  const getStatusClass = (status: LeaveStatus) => {
    const classes: Record<LeaveStatus, string> = {
      [LeaveStatus.PENDING]: 'status-pending',
      [LeaveStatus.APPROVED]: 'status-approved',
      [LeaveStatus.REJECTED]: 'status-rejected',
      [LeaveStatus.PENDING_HIERARCHY_APPROVAL]: 'status-pending',
    };
    return classes[status];
  };

  const getStatusText = (status: LeaveStatus): string => {
    if (status === LeaveStatus.PENDING_HIERARCHY_APPROVAL) {
      return t('leaves.statusOptions.pending');
    }
    return t(`leaves.statusOptions.${status}`);
  };

  const items = Object.values(LeaveStatus)
    .filter((s) => {
      if (s === LeaveStatus.PENDING_HIERARCHY_APPROVAL) return false;
      if (overallStatus === LeaveStatus.APPROVED) {
        return (
          s === LeaveStatus.APPROVED ||
          s === LeaveStatus.PENDING ||
          s === LeaveStatus.REJECTED
        );
      }
      if (overallStatus === LeaveStatus.REJECTED) {
        return s === LeaveStatus.REJECTED;
      }
      return true;
    })
    .map((value) => ({
      label: getStatusText(value as LeaveStatus),
      value,
      className: `status-option status-${value.toLowerCase()}`,
      disabled: value === displayStatus
  }));

  const selectStyles: React.CSSProperties = {
    width: '100%',
    backgroundColor: getStatusColor(displayStatus),
    color: '#fff',
    borderColor: getStatusColor(displayStatus),
    borderRadius: '4px',
    fontWeight: 'bold',
  };

  const selectClassName = `leave-status-select ${getStatusClass(displayStatus)}`;

  const handleStatusChange = (value: LeaveStatus) => {
    setSelectedValue(value);
    setIsPopconfirmVisible(true);
  };

  const handleConfirm = () => {
    if (selectedValue) {
      onStatusChange({ status: selectedValue });
    }
    setIsPopconfirmVisible(false);
  };

  const handleCancel = () => {
    setSelectedValue(null);
    setIsPopconfirmVisible(false);
  };

  const getConfirmMessage = (value: LeaveStatus | null) => {
    if (!value) return '';
    switch (value) {
      case 'pending': return 'İzin talebini tekrar beklemeye almak istediğinize emin misiniz?';
      case 'approved': return t('leaves.statusOptions.approvedText');
      case 'rejected': return t('leaves.statusOptions.rejectedText');
      default: return '';
    }
  };

  const getConfirmTitle = (value: LeaveStatus | null) => {
    if (!value) return '';
    switch (value) {
      case 'pending': return 'Beklemeye Al';
      case 'approved': return t('leaves.statusOptions.approvedTitle');
      case 'rejected': return t('leaves.statusOptions.rejectedTitle');
      default: return '';
    }
  };

  const getConfirmButtonText = (value: LeaveStatus | null) => {
    if (!value) return t('common.buttons.ok');
    switch (value) {
      case 'pending': return text ? 'Beklemeye Al' : <i className="ti ti-clock" />;
      case 'approved': return text ? t('leaves.statusOptions.approvedTitle') : <i className="ti ti-check"></i>;
      case 'rejected': return text ? t('leaves.statusOptions.rejectedTitle') : <i className="ti ti-x"></i>;
      default: return text ? t('common.buttons.ok') : <i className="ti ti-edit"></i>;
    }
  };

  const selectProps: SelectProps = {
    options: items,
    value: displayStatus,
    onChange: (value) => handleStatusChange(value as LeaveStatus),
    disabled: !isActionable,
    className: selectClassName,
    style: selectStyles,
    dropdownStyle: { minWidth: '150px' },
    popupClassName: 'leave-status-dropdown',
    bordered: true,
    showArrow: true,
  };

  return (
    <div className="leave-status-dropdown-container">
      <Popconfirm
        title={getConfirmTitle(selectedValue)}
        description={getConfirmMessage(selectedValue)}
        open={isPopconfirmVisible}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
        okText={getConfirmButtonText(selectedValue)}
        cancelText={t('common.buttons.cancel')}
        okButtonProps={{
          style: {
            backgroundColor: selectedValue ? getStatusColor(selectedValue) : undefined
          }
        }}
      >
        <Select {...selectProps} />
      </Popconfirm>
    </div>
  );
};

export default LeaveStatusDropdown;